import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { console_ } from './api'
import {
  type ConsolePreference,
  type ThemeChoice,
  type WidgetChoice,
  applyTheme,
  cachedTheme,
  defaultPreference,
  isThemeChoice
} from './preferences'

interface PreferencesValue {
  theme: ThemeChoice
  dashboardLayout: WidgetChoice[]
  /** True until the account's stored preference has arrived, so a screen can avoid saving over it. */
  loading: boolean
  setTheme: (choice: ThemeChoice) => void
  setDashboardLayout: (layout: WidgetChoice[]) => void
}

const PreferencesReference = createContext<PreferencesValue | null>(null)

/**
 * The one piece of state every screen shares.
 *
 * This is the app's first React context, and it is here rather than in `App.tsx` state for a reason
 * worth stating: the theme is read by the settings screen that changes it and the dashboard that has
 * to redraw its charts, and passing a setter down through two unrelated route trees to join them
 * would be worse than the provider. Everything else in this console stays local state.
 *
 * The optimistic order matters. A theme change is applied to the document immediately and saved
 * afterwards, because the operator is looking at the thing they just changed: waiting for a round
 * trip to repaint makes a toggle feel broken on a slow connection. A save that fails leaves the
 * screen as chosen and the row as it was, and the next load reverts — which is the honest outcome,
 * and far better than snapping the palette back under somebody mid-click.
 */
export function PreferencesProvider({ children }: { children: React.ReactNode }) {
  // Seeded from the same cache the pre-paint script in index.html read, so the first render agrees
  // with what is already on screen rather than flashing past it.
  const [theme, setThemeState] = useState<ThemeChoice>(() => cachedTheme())
  const [dashboardLayout, setLayoutState] = useState<WidgetChoice[]>([])
  const [loading, setLoading] = useState(true)

  // Held in a ref so a save started by one change carries the other's current value without either
  // setter having to depend on the other and re-create itself on every keystroke.
  const latest = useRef<ConsolePreference>(defaultPreference())
  latest.current = { theme, dashboardLayout }

  useEffect(() => {
    let alive = true

    console_
      .get()
      .then((stored) => {
        if (!alive) return
        const choice = isThemeChoice(stored.theme) ? stored.theme : 'system'
        setThemeState(choice)
        applyTheme(choice)
        setLayoutState(stored.dashboardLayout ?? [])
      })
      .catch(() => {
        // The preference could not be read. The cached theme is already applied and the dashboard
        // falls back to its default arrangement, so there is nothing to tell the operator about and
        // nothing they could do with it — the console works, it simply has not remembered anything.
      })
      .finally(() => {
        if (alive) setLoading(false)
      })

    return () => {
      alive = false
    }
  }, [])

  const save = useCallback((next: ConsolePreference) => {
    void console_.save(next).catch(() => {
      // Deliberately silent. See the note on optimistic ordering above.
    })
  }, [])

  const setTheme = useCallback(
    (choice: ThemeChoice) => {
      applyTheme(choice)
      setThemeState(choice)
      save({ theme: choice, dashboardLayout: latest.current.dashboardLayout })
    },
    [save]
  )

  const setDashboardLayout = useCallback(
    (layout: WidgetChoice[]) => {
      setLayoutState(layout)
      save({ theme: latest.current.theme, dashboardLayout: layout })
    },
    [save]
  )

  const value = useMemo(
    () => ({ theme, dashboardLayout, loading, setTheme, setDashboardLayout }),
    [theme, dashboardLayout, loading, setTheme, setDashboardLayout]
  )

  return <PreferencesReference value={value}>{children}</PreferencesReference>
}

export function usePreferences(): PreferencesValue {
  const value = useContext(PreferencesReference)
  if (!value) throw new Error('usePreferences was called outside PreferencesProvider.')
  return value
}
